// Firebase configuration and initialization
import { initializeApp, getApps } from 'firebase/app';
import { getAuth, GoogleAuthProvider } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { firebaseConfig } from './firebaseConfig';

// Firebase app instance
let app = null;
let auth = null;
let db = null;
let googleProvider = null;
let initializationPromise = null;

// Function to load config from JSON file
function loadConfigFromFile() {
  return fetch('/firebase-config.json')
    .then(response => {
      if (!response.ok) {
        throw new Error(`Failed to load firebase-config.json: ${response.status}`);
      }
      return response.json();
    })
    .then(config => {
      if (!config.apiKey || !config.projectId) {
        throw new Error('Invalid Firebase config: missing apiKey or projectId');
      }
      if (config.apiKey === 'YOUR_FIREBASE_API_KEY' || config.apiKey.includes('YOUR_')) {
        throw new Error('Invalid Firebase config: API key appears to be a placeholder. Please update firebase-config.json with your actual Firebase credentials.');
      }
      return config;
    })
    .catch(error => {
      throw error;
    });
}

// Function to initialize Firebase
function initializeFirebase(config) {
  if (app) {
    return app; // Already initialized
  }

  try {
    // Validate config before initializing
    if (!config.apiKey || config.apiKey === 'YOUR_FIREBASE_API_KEY' || config.apiKey.includes('YOUR_')) {
      throw new Error('Invalid API key: appears to be a placeholder. Please check firebase-config.json');
    }
    
    app = initializeApp(config);
    auth = getAuth(app);
    db = getFirestore(app);
    googleProvider = new GoogleAuthProvider();
    return app;
  } catch (error) {
    throw error;
  }
}

// Initialize Firebase based on available config
function ensureInitialized() {
  if (initializationPromise) {
    return initializationPromise;
  }

  const apps = getApps();
  if (apps.length > 0) {
    // Firebase already initialized (e.g., by another module)
    app = apps[0];
    auth = getAuth(app);
    db = getFirestore(app);
    googleProvider = new GoogleAuthProvider();
    initializationPromise = Promise.resolve(app);
    return initializationPromise;
  }

  // Check if we have valid config from environment variables
  if (firebaseConfig.apiKey && firebaseConfig.projectId && firebaseConfig.apiKey !== "") {
    // Use config from environment variables
    try {
      initializeFirebase(firebaseConfig);
      initializationPromise = Promise.resolve(app);
      return initializationPromise;
    } catch (error) {
      initializationPromise = Promise.reject(error);
      return initializationPromise;
    }
  } else {
    // Load config from JSON file
    initializationPromise = loadConfigFromFile()
      .then(config => {
        initializeFirebase(config);
        return app;
      })
      .catch(error => {
        throw error;
      });
    return initializationPromise;
  }
}

// Start initialization immediately
ensureInitialized();

// Export auth with lazy initialization check
export const getAuthInstance = () => {
  if (!auth) {
    if (app) {
      auth = getAuth(app);
    } else {
      throw new Error('Firebase Auth not initialized. Please wait for config to load.');
    }
  }
  return auth;
};

// Export Firestore with lazy initialization check
export const getFirestoreInstance = () => {
  if (!db) {
    if (app) {
      db = getFirestore(app);
    } else {
      throw new Error('Firebase Firestore not initialized. Please wait for config to load.');
    }
  }
  return db;
};

// Export provider with lazy initialization
export const getGoogleProvider = () => {
  if (!googleProvider) {
    googleProvider = new GoogleAuthProvider();
  }
  return googleProvider;
};

// For backward compatibility, export auth directly (but it might be null initially)
// These will be set once initialization completes
export { auth };

// Export Firestore directly (but it might be null initially)
export { db };

// Export provider
export { googleProvider };

// Export app
export default app;

// Export initialization promise for components that need to wait
export { initializationPromise };
